import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { loadAnalyticsScripts, loadMarketingScripts } from '../utilities/cookieConsentUtils';
import '../customCSS/CookieConsent.css';

interface CookieConsent {
    analytics: boolean;
    marketing: boolean;
    essential: boolean;
}

const CookieConsent: React.FC = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [consent, setConsent] = useState<CookieConsent>({
        analytics: false,
        marketing: false,
        essential: true, // Always required
    });

    // Check if user has already given consent
    useEffect(() => {
        const storedConsent = localStorage.getItem('cookieConsent');
        if (!storedConsent) {
            setShowBanner(true);
        } else {
            // Parse stored preferences and restore state
            try {
                const parsedConsent = JSON.parse(storedConsent) as CookieConsent;
                setConsent(parsedConsent);
                setShowBanner(false);
                // Load scripts based on restored preferences
                if (parsedConsent.analytics) {
                    loadAnalyticsScripts();
                }
                if (parsedConsent.marketing) {
                    loadMarketingScripts();
                }
            } catch (error) {
                console.error('Failed to parse stored consent:', error);
                setShowBanner(true);
            }
        }
    }, []);

    const handleAcceptAll = () => {
        const fullConsent: CookieConsent = {
            analytics: true,
            marketing: true,
            essential: true,
        };
        saveCookieConsent(fullConsent);
    };

    const handleRejectAll = () => {
        const minimalConsent: CookieConsent = {
            analytics: false,
            marketing: false,
            essential: true,
        };
        saveCookieConsent(minimalConsent);
    };

    const handleSavePreferences = () => {
        saveCookieConsent(consent);
    };

    const saveCookieConsent = (consentData: CookieConsent) => {
        localStorage.setItem('cookieConsent', JSON.stringify(consentData));
        localStorage.setItem('cookieConsentTimestamp', new Date().toISOString());
        setShowBanner(false);
        setShowDetails(false);

        // Load analytics/marketing scripts based on consent
        if (consentData.analytics) {
            loadAnalyticsScript();
        }
        if (consentData.marketing) {
            loadMarketingScript();
        }
    };

    const loadAnalyticsScript = () => {
        // Example: Google Analytics
        // You can add your analytics script here
        console.log('Analytics cookies enabled');
        // If using Google Analytics:
        // window.dataLayer = window.dataLayer || [];
        // gtag('consent', 'update', { 'analytics_storage': 'granted' });
    };

    const loadMarketingScript = () => {
        // Example: Facebook Pixel or other marketing tools
        console.log('Marketing cookies enabled');
        // Add your marketing script here
    };

    if (!showBanner) return null;

    return (
        <>
            {/* Cookie Consent Banner */}
            <div className="cookie-consent-banner">
                <div className="cookie-consent-container">
                    <div className="cookie-consent-content">
                        <div className="cookie-consent-header">
                            <h4 className="cookie-consent-title">🍪 Cookie Consent</h4>
                            <button
                                className="cookie-consent-close"
                                onClick={() => setShowBanner(false)}
                                aria-label="Dismiss cookie banner"
                                title="Dismiss without saving preferences"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <p className="cookie-consent-description">
                            We use cookies to enhance your browsing experience, serve personalized ads or content,
                            and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.
                        </p>

                        <button
                            className="cookie-consent-link"
                            onClick={() => setShowDetails(!showDetails)}
                        >
                            {showDetails ? 'Hide' : 'Show'} Cookie Details
                        </button>

                        {showDetails && (
                            <div className="cookie-consent-details">
                                <div className="cookie-type">
                                    <div className="cookie-type-header">
                                        <input
                                            type="checkbox"
                                            id="essential"
                                            checked={true}
                                            disabled
                                        />
                                        <label htmlFor="essential">
                                            <strong>Essential Cookies</strong> (Required)
                                        </label>
                                    </div>
                                    <p className="cookie-description">
                                        These cookies are necessary for the website to function properly. They enable
                                        basic functionalities like navigation and security.
                                    </p>
                                </div>

                                <div className="cookie-type">
                                    <div className="cookie-type-header">
                                        <input
                                            type="checkbox"
                                            id="analytics"
                                            checked={consent.analytics}
                                            onChange={(e) =>
                                                setConsent({ ...consent, analytics: e.target.checked })
                                            }
                                        />
                                        <label htmlFor="analytics">
                                            <strong>Analytics Cookies</strong> (Optional)
                                        </label>
                                    </div>
                                    <p className="cookie-description">
                                        These cookies help us understand how visitors interact with our website,
                                        allowing us to improve functionality and user experience.
                                    </p>
                                </div>

                                <div className="cookie-type">
                                    <div className="cookie-type-header">
                                        <input
                                            type="checkbox"
                                            id="marketing"
                                            checked={consent.marketing}
                                            onChange={(e) =>
                                                setConsent({ ...consent, marketing: e.target.checked })
                                            }
                                        />
                                        <label htmlFor="marketing">
                                            <strong>Marketing Cookies</strong> (Optional)
                                        </label>
                                    </div>
                                    <p className="cookie-description">
                                        These cookies are used to track visitors across websites and deliver personalized
                                        advertisements based on their interests.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="cookie-consent-actions">
                        <button
                            className="cookie-consent-btn reject"
                            onClick={handleRejectAll}
                        >
                            Reject All
                        </button>
                        {showDetails && (
                            <button
                                className="cookie-consent-btn save"
                                onClick={handleSavePreferences}
                            >
                                Save Preferences
                            </button>
                        )}
                        <button
                            className="cookie-consent-btn accept"
                            onClick={handleAcceptAll}
                        >
                            Accept All
                        </button>
                    </div>
                </div>
            </div>

            {/* Backdrop */}
            {showBanner && <div className="cookie-consent-backdrop" />}
        </>
    );
};

export default CookieConsent;
